/**
 * Bulk company upload results (search category `companyBulkSearch`) reuse the
 * profiler row shape, but their `score` is the name matcher's similarity and
 * their `rationale` holds the company name(s) the user uploaded. Neither is an
 * AI relevance judgement, so a bulk row must never render the AI match badge.
 * The uploaded name is shown in the bulk upload review step only.
 *
 * Deliberately independent of `VITE_PROFILER_SCORING_ENABLED` and of
 * `mapCompanyScoring`: the badge is suppressed whatever the scoring flag says,
 * and AI search rows are never touched. It keys on the search category, so it
 * also holds for bulk rows that still carry a score (before the TD-2991
 * migration has run in an environment).
 */

// Everything mapCompanyScoring writes for the badge. `scoreLabel` matters
// beyond the badge itself: ProfilerBarChart shows its "Rationale" row as soon
// as any row carries one, and the "High matches" spotlight reads it.
const BADGE_FIELDS = [
  'scoreLabel',
  'scoreTooltipText',
  'scorePrimaryColor',
  'scoreSecondaryColor',
  'scoreTooltipClassName',
  'scoreIconFilter'
];

/**
 * Removes the AI match badge from one mapped bulk result row, in place.
 *
 * @param {object} row A row already run through mapCompanyEntry.
 * @returns {object} The same row.
 */
export const hideBulkMatchBadge = (row) => {
  if (!row || typeof row !== 'object') {
    return row;
  }

  row.showScoring = false;
  BADGE_FIELDS.forEach((field) => {
    delete row[field];
  });

  return row;
};

/**
 * Removes the badge from every row of a bulk result set, in place. Call it
 * before the rows are handed to a MobX store, so it edits the plain objects.
 *
 * @param {object[]} rows Mapped bulk result rows.
 * @returns {object[]} The same array (an empty array for non-array input).
 */
export const hideBulkMatchBadgeRows = (rows) => {
  if (!Array.isArray(rows)) {
    return [];
  }
  rows.forEach(hideBulkMatchBadge);
  return rows;
};
